import React, { useState, useEffect } from 'react';
import { CheckCircle, ArrowRight, Shield, Zap, Users } from 'lucide-react';
import Navbar from './Navbar';
import MachineMarketfilter from './MachineMarketfilter';
import CTA from './CTA';
import Footer from './Footer';
import cnc from '../image/cnc.webp'
import pcb from '../image/pcb.jpg'
import robo from '../image/robo.jpg'

const AbnerComponent = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  
  // Background images for carousel
  const backgroundImages = [
  cnc,
  pcb,
  robo,
];

  // Auto-scroll effect
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % backgroundImages.length);
    }, 4000); // Change slide every 4 seconds

    return () => clearInterval(interval);
  }, [backgroundImages.length]);

  // Manual navigation
  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  return (
    <>
      <Navbar />
      <section className="relative w-full overflow-hidden font-sans antialiased min-h-[450px] md:h-[450px]">
        {/* ---------- BACKGROUND IMAGE CAROUSEL ---------- */}
        <div className="absolute inset-0 z-0">
          {backgroundImages.map((img, index) => (
            <div
              key={index}
              className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
                index === currentSlide ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <img
                src={img}
                alt={`Industrial machinery background ${index + 1}`}
                className="h-full w-full object-cover object-center"
                loading={index === 0 ? "eager" : "lazy"}
              />
            </div>
          ))}
          
          {/* dark gradient overlays for depth & readability */}
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/80 via-slate-800/50 to-slate-900/30" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20" />
          
          {/* premium accent glow */}
          <div className="absolute -top-20 -right-20 h-96 w-96 rounded-full bg-blue-600/20 blur-3xl" />
          <div className="absolute bottom-0 left-0 h-64 w-64 rounded-full bg-cyan-500/10 blur-2xl" />
        </div>

        {/* ---------- SLIDE INDICATORS (dots) ---------- */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 flex gap-2">
          {backgroundImages.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`transition-all duration-300 rounded-full ${
                index === currentSlide
                  ? 'w-8 h-2 bg-white'
                  : 'w-2 h-2 bg-white/50 hover:bg-white/80'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>

        {/* ---------- CONTENT (on top of background) ---------- */}
        <div className="relative z-10 flex h-full min-h-[450px] items-center px-4 sm:px-6 lg:px-8 py-6 md:py-0">
          <div className="mx-auto w-full max-w-7xl">
            <div className="flex flex-col lg:flex-row items-center gap-6 md:gap-8 lg:gap-12">
              
              {/* ---------- LEFT SIDE: TEXT CONTENT ---------- */}
              <div className="flex-1 w-full lg:max-w-xl space-y-3 md:space-y-4 text-white">
                {/* Badge / tag */}
                <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md text-white text-[10px] sm:text-xs font-semibold px-2.5 sm:px-3 py-1 rounded-full border border-white/20 shadow-lg shadow-black/10">
                  <Shield size={12} className="text-cyan-300" />
                  <span>Next‑gen platform</span>
                </div>

                {/* Heading */}
                <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.1]">
                  <span className="block text-white drop-shadow-lg">342+ Machines.</span>
                  <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-300 via-cyan-200 to-blue-400 drop-shadow-lg">
                    One platform.
                  </span>
                </h1>

                {/* Description */}
                <p className="text-xs sm:text-sm md:text-base text-white/90 leading-relaxed max-w-lg drop-shadow-md">
                  Unify your entire machine ecosystem. From edge devices to cloud 
                  analytics — all in one secure, intelligent workspace.
                </p>

                {/* Feature bullets - hidden on very small screens, shown on sm and up */}
                <ul className="space-y-1.5 sm:space-y-2 text-white/95 hidden sm:block">
                  <li className="flex items-start gap-2.5">
                    <CheckCircle size={16} className="text-cyan-300 mt-0.5 flex-shrink-0 drop-shadow" />
                    <span className="text-xs sm:text-sm md:text-base font-medium drop-shadow">Centralized device orchestration</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <Zap size={16} className="text-cyan-300 mt-0.5 flex-shrink-0 drop-shadow" />
                    <span className="text-xs sm:text-sm md:text-base font-medium drop-shadow">Real‑time performance insights</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <Users size={16} className="text-cyan-300 mt-0.5 flex-shrink-0 drop-shadow" />
                    <span className="text-xs sm:text-sm md:text-base font-medium drop-shadow">Role‑based access for teams</span>
                  </li>
                </ul>

                {/* Feature bullets - simplified for mobile */}
                <ul className="space-y-1 text-white/95 sm:hidden">
                  <li className="flex items-start gap-2">
                    <CheckCircle size={14} className="text-cyan-300 mt-0.5 flex-shrink-0 drop-shadow" />
                    <span className="text-[11px] font-medium drop-shadow">Centralized orchestration</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Zap size={14} className="text-cyan-300 mt-0.5 flex-shrink-0 drop-shadow" />
                    <span className="text-[11px] font-medium drop-shadow">Real-time insights</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Users size={14} className="text-cyan-300 mt-0.5 flex-shrink-0 drop-shadow" />
                    <span className="text-[11px] font-medium drop-shadow">Team access control</span>
                  </li>
                </ul>

                {/* CTA + premium hint */}
                <div className="flex flex-wrap items-center gap-2 sm:gap-3 pt-1">
                  <button className="group inline-flex items-center gap-2 bg-white hover:bg-blue-50 text-slate-900 font-semibold px-4 sm:px-6 py-2 sm:py-2.5 rounded-xl shadow-2xl shadow-blue-900/40 transition-all duration-200 hover:shadow-blue-700/30 hover:scale-[1.02] backdrop-blur-sm text-xs sm:text-sm">
                    <span>Get started</span>
                    <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                  </button>
                  
                </div>

                {/* small trust line */}
                <p className="text-[9px] sm:text-[10px] text-white/70 flex items-center gap-1.5 drop-shadow">
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400"></span>
                  Trusted by 2,000+ industrial teams
                </p>
              </div>

              {/* ---------- RIGHT SIDE: IMAGE CARD (hidden on mobile, visible on md+) ---------- */}
              <div className="hidden md:flex flex-1 w-full lg:max-w-lg justify-center lg:justify-end">
                <div className="relative w-full max-w-sm md:max-w-md">
                  
                
                  {/* small floating dot – design detail */}
                  <div className="absolute -top-2 -left-2 w-12 h-12 bg-blue-400/30 rounded-full blur-2xl -z-10" />
                  <div className="absolute -bottom-6 -right-6 w-20 h-20 bg-cyan-300/20 rounded-full blur-3xl -z-10" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <div className="mb-5 mt-6 md:mb-[20px] md:mt-[30px]">
        <MachineMarketfilter />
        <CTA />
        <Footer />
      </div>
    </>
  );
};

export default AbnerComponent;